import { skipToken } from '@tanstack/react-query';

import { type Leistung, type Veranstaltung, type Zeitraum } from '../../../../../dtos';
import { trpc } from '../../../../../trpc';
import { formatTimeRangeForDate } from '../../../../../utils';
import {
  getAllLeistungen,
  getSortedModulesForLeistung,
  type TeilleistungColumn,
} from '../../../../../utils/teilleistung';
import { AusschreibungRueckmeldungenCard } from '../../../Ausschreibung/AusschreibungRueckmeldungenCard';
import { AusschreibungsprozessCard } from '../../../Ausschreibung/AusschreibungsprozessCard';
import { shouldHideAusschreibungCards } from '../../../Ausschreibung/shouldHideAusschreibungCards.js';
import { FailedQueryAlert } from '../../../ErrorAlert';
import { type VorschlaegePruefenSelection } from '../VorschlaegePruefenCalendar.types.js';

export type { VorschlaegePruefenSelection };

export type ExperteAntwortenVeranstaltung = Pick<
  Veranstaltung,
  'teilleistungen' | 'akaDurchgangId' | 'ablauf' | 'vivaStatus'
>;

type ExperteAntwortenProps = Readonly<{
  veranstaltung: ExperteAntwortenVeranstaltung | null;
  isVorschlaegePruefenMode?: boolean;
  vorschlagSelection?: VorschlaegePruefenSelection;
}>;

const formatAblaufTage = (ablauf: readonly Zeitraum[]) =>
  ablauf.map(({ start, end }) => formatTimeRangeForDate(start, end, true)).join(', ');

export const ExperteAntworten = function ({
  veranstaltung,
  isVorschlaegePruefenMode = false,
  vorschlagSelection,
}: ExperteAntwortenProps) {
  const akaDurchgangId = veranstaltung?.akaDurchgangId;
  const durchgangQuery = trpc.durchgang.getDurchgangByAkaDurchgangId.useQuery(
    akaDurchgangId ? { akaDurchgangId } : skipToken,
  );

  if (!veranstaltung) {
    return null;
  }

  if (durchgangQuery.isError) {
    return <FailedQueryAlert error={durchgangQuery.error} query="Durchgang" />;
  }

  const modultermine = akaDurchgangId ? durchgangQuery.data?.modultermine : undefined;
  const leistungen = modultermine
    ? getAllLeistungen(modultermine.flatMap((modultermin) => modultermin.teilleistungen))
    : getAllLeistungen(veranstaltung.teilleistungen);

  if (leistungen.length === 0) {
    return null;
  }

  const vivaStatusForHidingAusschreibungCards = durchgangQuery.isEnabled
    ? durchgangQuery.data?.vivaStatus
    : veranstaltung.vivaStatus;

  const hideAusschreibungPlanungCards =
    !isVorschlaegePruefenMode && shouldHideAusschreibungCards(vivaStatusForHidingAusschreibungCards);

  if (hideAusschreibungPlanungCards) {
    return null;
  }

  const datePerTeilleistung = new Map<string, string>(
    (modultermine ?? []).flatMap((modultermin) =>
      modultermin.teilleistungen.map(
        (teilleistung) => [teilleistung.teilleistungId, formatAblaufTage(teilleistung.ablauf)] as const,
      ),
    ),
  );
  const activeTeilleistungAblauf = veranstaltung.teilleistungen.find(
    (teilleistung) => teilleistung.teilleistungId === vorschlagSelection?.activeTeilleistungId,
  )?.ablauf;

  const getTeilleistungColumns = (leistungId: Leistung['leistungId']): TeilleistungColumn[] | undefined => {
    if (!modultermine) {
      return undefined;
    }

    const spalten = getSortedModulesForLeistung(modultermine, leistungId);

    return isVorschlaegePruefenMode
      ? spalten.map((spalte): TeilleistungColumn => ({
          ...spalte,
          date: datePerTeilleistung.get(spalte.teilleistungId),
        }))
      : spalten;
  };

  return (
    <>
      {leistungen.map((leistung) => (
        <div
          className={
            isVorschlaegePruefenMode ? 'grid grid-cols-1 gap-16' : 'grid grid-cols-1 gap-16 lg:grid-cols-[1fr_2fr]'
          }
          key={leistung.leistungId}
        >
          {!isVorschlaegePruefenMode && <AusschreibungsprozessCard leistung={leistung} showStatusActions={false} />}
          <AusschreibungRueckmeldungenCard
            leistung={leistung}
            teilleistungColumns={getTeilleistungColumns(leistung.leistungId)}
            showLeistungTag={isVorschlaegePruefenMode}
            date={
              isVorschlaegePruefenMode && !modultermine && activeTeilleistungAblauf
                ? formatAblaufTage(activeTeilleistungAblauf)
                : undefined
            }
            vorschlagSelection={vorschlagSelection?.leistungId === leistung.leistungId ? vorschlagSelection : undefined}
          />
        </div>
      ))}
    </>
  );
};
